import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { find, forEach } from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import Pluralize from 'pluralize';
import { NgClass } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';
import { GridColumn, TableComponent, TarkNumberPipe, TemplateColumn } from '@tarktech/tark-ng-utils';

import { exclamationTriangle, inventorylogprint, inventorylogupdate } from 'src/app/shared/components/icon/icons';
import { InventoryProductRecipeService } from 'src/app/shared/services/inventory-product-recipe.service';
import { InventoryProductService } from 'src/app/shared/services/inventory-product.service';
import { AlertsService } from 'src/app/shared/services/alerts.service';
import { ApplicationStateService } from 'src/app/shared/services/application-state.service';
import { SpinnerService } from 'src/app/shared/components/spinner/spinner.service';
import { ModalService } from 'src/app/shared/components/modal/modal.service';
import { InfoModalComponent } from 'src/app/shared/components/info-modal/info-modal.component';
import { Messages } from 'src/app/shared/constants/ui-messages';
import { StringUtils } from 'src/app/shared/string-utils/string-utils';
import { SettingParam } from 'src/app/shared/interface/setting-param';
import { InvProduct } from 'src/app/shared/interface/inventory-product';
import { InventoryProductRecipe } from 'src/app/information-management/inventory-products/interface/inventory-product-recipe';
import { InventoryProductSummary } from 'src/app/information-management/inventory-products/interface/inventory-product-summary';

import { EditWrapperComponent } from '../../../../../shared/components/edit-wrapper/edit-wrapper.component';
import { RowComponent } from '../../../../../shared/components/grid/row/row.component';
import { ColumnComponent } from '../../../../../shared/components/grid/column/column.component';
import { FormFieldComponent } from '../../../../../shared/components/form-controls/form-field/form-field.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SelectOptionOnTabDirective } from '../../../../../shared/components/form-controls/directives/select-option-on-tab.directive';
import { InputControlDirective } from '../../../../../shared/components/form-controls/directives/input-control.directive';
import { InputDirective } from '../../../../../shared/components/input/input.directive';
import { PosDecimalDirective } from '../../../../../shared/components/pos-decimal.directive';
import { RangeDirective } from '../../../../../shared/components/form-controls/directives/validators/range.directive';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { RecordInventoryPreparation } from '../../interfaces/record-inventory-preparation';
import { VariableIngredientRecipeService } from '../../services/variable-ingredient-recipe.service';
import { InventoryPrepType } from 'src/app/shared/interface/inventory-prep-type';

@Component({
    selector: 'pos-inventory-prep',
    templateUrl: './inventory-prep.component.html',
    styleUrls: ['./inventory-prep.component.scss'],
    imports: [FormsModule, NgClass, EditWrapperComponent, RowComponent, ColumnComponent, FormFieldComponent,
        NgSelectComponent, SelectOptionOnTabDirective, InputControlDirective, InputDirective, PosDecimalDirective,
        RangeDirective, IconComponent, Tooltip, TableComponent, TarkNumberPipe]
})
export class InventoryPrepComponent implements OnInit {

  prepProducts: Array<InventoryProductSummary> = [];
  selectedProductId: number = null;
  selectedProduct: InventoryProductSummary = null;
  recipeList: Array<InventoryProductRecipe> = [];
  recipeInformation = '';
  isRecipeLoaded = false;

  // Fixed
  qty: number = null;
  unitName = '';

  // Variable
  prepareQty = 0;        // recipe-predicted yield (limiting ingredient)
  madeQty: number = null; // actual produced qty (user-editable, defaults to prepareQty)
  variableUnit = '';
  isFormSubmitted = false;

  settingParam: SettingParam;
  icons = { exclamationTriangle, inventorylogprint, inventorylogupdate };
  ingredientColumns: Array<GridColumn> = [];
  @ViewChild('prepForm') prepForm: NgForm;
  @ViewChild('ingredientTemplate', { static: true }) ingredientTemplate: TemplateRef<any>;
  @ViewChild('neededTemplate', { static: true }) neededTemplate: TemplateRef<any>;
  @ViewChild('usedTemplate', { static: true }) usedTemplate: TemplateRef<any>;
  @ViewChild('onHandTemplate', { static: true }) onHandTemplate: TemplateRef<any>;

  constructor(private inventoryProductService: InventoryProductService,
    private inventoryProductRecipeService: InventoryProductRecipeService,
    private variableIngredientRecipeService: VariableIngredientRecipeService,
    private applicationStateService: ApplicationStateService,
    private alertsService: AlertsService,
    private spinnerService: SpinnerService,
    private modalService: ModalService,
    private router: Router) { }

  ngOnInit(): void {
    this.settingParam = this.applicationStateService.settingParam;
    this.loadProducts();
  }

  private buildColumns() {
    // Column set for BOTH modes: Ingredient · Qty · Unit · Available Qty.
    // Only the Qty column differs — read-only "Needed Qty" (Fixed) vs editable "Qty" (Variable).
    const qtyColumn = this.isVariable
      ? new TemplateColumn({ HeaderText: 'Qty', IsSortable: false, Width: '30%', itemTemplate: this.usedTemplate })
      : new TemplateColumn({ HeaderText: 'Needed Qty', IsSortable: false, Width: '30%', itemTemplate: this.neededTemplate });
    this.ingredientColumns = [
      new TemplateColumn({ HeaderText: 'Ingredient', IsSortable: false, Width: '35%', itemTemplate: this.ingredientTemplate }),
      qtyColumn,
      new GridColumn({ HeaderText: 'Unit', Field: 'UnitName', IsSortable: false, Width: '15%' }),
      new TemplateColumn({ HeaderText: 'Available Qty', IsSortable: false, Width: '20%', itemTemplate: this.onHandTemplate })
    ];
  }

  get isVariable(): boolean {
    return this.selectedProduct?.PrepType === InventoryPrepType.Variable;
  }

  get hasSelection(): boolean {
    return !!this.selectedProduct;
  }

  loadProducts() {
    this.spinnerService.show();
    this.inventoryProductService.getInventoryPrepRecordingProducts()
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (response) => { this.prepProducts = response; },
        error: this.alertsService.showApiError
      });
  }

  onProductChange() {
    this.resetSelection();
    if (!this.selectedProductId) {
      this.selectedProduct = null;
      return;
    }
    this.selectedProduct = find(this.prepProducts, x => x.Id === this.selectedProductId);
    if (!this.selectedProduct) {
      return;
    }
    this.spinnerService.show();
    const observable: Array<Observable<any>> = [
      this.inventoryProductService.getInventoryProduct(this.selectedProductId),
      this.inventoryProductRecipeService.getInventoryProductRecipe(this.selectedProductId)
    ];
    forkJoin(observable)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: ([inventoryProduct, recipes]: [InvProduct, Array<InventoryProductRecipe>]) => {
          this.recipeInformation = inventoryProduct?.Recipe ?? '';
          this.recipeList = recipes ?? [];
          forEach(this.recipeList, (recipe) => {
            if (!recipe.PrepareQty) { recipe.PrepareQty = 1; }
            recipe.UsedPrepQty = 0;
          });
          this.isRecipeLoaded = true;
          if (this.isVariable) {
            this.calculatePrepQty();
          } else {
            this.qty = 1;
            this.updateUnitName();
          }
          this.buildColumns();
        },
        error: this.alertsService.showApiError
      });
  }

  // ----- Fixed helpers -----
  updateUnitName() {
    if (this.selectedProduct) {
      this.unitName = this.qty ? Pluralize(this.selectedProduct.UnitName, this.qty) : this.selectedProduct.UnitName;
    }
  }

  getNeeded(recipe: InventoryProductRecipe): number {
    const qty = this.num(this.qty);
    return qty * (recipe.Qty / (recipe.PrepareQty || 1));
  }

  isShort(recipe: InventoryProductRecipe): boolean {
    return !recipe.IsSkipInventory && this.getNeeded(recipe) > recipe.CalculatedQty;
  }

  // Available qty after this batch consumes the ingredient (shown live in the grid).
  getAfter(recipe: InventoryProductRecipe): number {
    return recipe.CalculatedQty - this.getNeeded(recipe);
  }

  // ----- Variable helpers -----
  getPrepareQty(recipe: InventoryProductRecipe): number {
    const perUnit = parseFloat((recipe.Qty / recipe.PrepareQty).toFixed(2));
    return Math.floor(parseFloat((this.num(recipe.UsedPrepQty) / (perUnit > 0 ? perUnit : 1)).toFixed(2)));
  }

  calculatePrepQty() {
    this.prepareQty = 0;
    let isPositiveQty = false;
    forEach(this.recipeList, (recipe) => {
      const perUnit = parseFloat((recipe.Qty / recipe.PrepareQty).toFixed(2));
      if (!isPositiveQty) {
        if (perUnit > 0) {
          this.prepareQty = this.getPrepareQty(recipe);
          isPositiveQty = true;
        }
      } else if (this.num(recipe.UsedPrepQty) > 0) {
        if (perUnit > 0) {
          const candidate = this.getPrepareQty(recipe);
          this.prepareQty = this.prepareQty > candidate ? candidate : this.prepareQty;
        }
      } else {
        this.prepareQty = 0;
      }
    });
    if (this.selectedProduct) {
      this.variableUnit = this.prepareQty ? Pluralize(this.selectedProduct.UnitName, this.prepareQty)
        : this.selectedProduct.UnitName;
    }
    this.madeQty = this.prepareQty;
  }

  getAfterVar(recipe: InventoryProductRecipe): number {
    return recipe.CalculatedQty - this.num(recipe.UsedPrepQty);
  }

  isShortVar(recipe: InventoryProductRecipe): boolean {
    return !recipe.IsSkipInventory && this.getAfterVar(recipe) < 0;
  }

  // Available qty shown in the grid, clamped so it never displays a negative value.
  getAvailableQty(recipe: InventoryProductRecipe): number {
    const remaining = this.isVariable ? this.getAfterVar(recipe) : this.getAfter(recipe);
    return remaining > 0 ? remaining : 0;
  }

  get anyShort(): boolean {
    if (!this.isRecipeLoaded) { return false; }
    return this.isVariable ? this.recipeList.some(r => this.isShortVar(r))
      : this.recipeList.some(r => this.isShort(r));
  }

  get recordButtonLabel(): string {
    return this.anyShort ? 'Record Anyway' : 'Record Prep';
  }

  // ----- Commit -----
  recordPrep() {
    if (!this.hasSelection) { return; }
    this.isFormSubmitted = true;
    if (this.prepForm?.invalid) { return; }
    if (this.isVariable) {
      this.recordVariable();
    } else {
      this.recordFixed();
    }
  }

  private recordFixed() {
    if (!this.num(this.qty)) {
      return;
    }
    if (this.anyShort) {
      this.confirm(StringUtils.format(Messages.ConfirmSaveRecipeIngredients, { 'Name': this.selectedProduct.Name }),
        () => this.confirmPrepLabelPrint(() => this.saveFixed(false)));
    } else {
      this.confirmPrepLabelPrint(() => this.saveFixed(false));
    }
  }

  private recordVariable() {
    this.isFormSubmitted = true;
    if (!this.num(this.madeQty)) {
      return;
    }
    if (this.recipeList.some(r => (!r.UsedPrepQty && r.UsedPrepQty !== 0) || r.UsedPrepQty < 0)) {
      return;
    }
    if (this.anyShort) {
      this.confirm(StringUtils.format(Messages.ConfirmSaveRecipeIngredients, { 'Name': this.selectedProduct.Name }),
        () => this.confirmPrepLabelPrint(() => this.saveVariable(false)));
    } else {
      this.confirmPrepLabelPrint(() => this.saveVariable(false));
    }
  }

  private confirmPrepLabelPrint(onDone: (printLabels: boolean) => void) {
    const labelQty = this.isVariable ? this.prepareQty : this.num(this.qty);
    if (this.selectedProduct?.IsPrintPrepLabel && labelQty > 0) {
      if (!this.settingParam.MaxPrepLabels) { this.settingParam.MaxPrepLabels = 15; }
      const printCount = labelQty > this.settingParam.MaxPrepLabels ? this.settingParam.MaxPrepLabels : labelQty;
      const modalRef = this.modalService.getModalWrapper(InfoModalComponent);
      const modal = modalRef.show({
        animated: false,
        class: 'vertical-center',
        initialState: {
          message: StringUtils.format(Messages.ConfirmPrepLabelPrint, { 'printCount': printCount }),
          confirmButtonText: 'Yes',
          rejectButtonText: 'No',
          modalHeaderText: 'Confirm'
        }
      });
      modal.close.subscribe((response) => {
        if (response) { onDone(response.shouldConfirm); }
      });
    } else {
      onDone(false);
    }
  }

  private saveFixed(isPrintPrepLabels: boolean) {
    const payload: RecordInventoryPreparation = {
      InventoryProductId: this.selectedProductId,
      Qty: this.num(this.qty),
      Name: this.selectedProduct.Name,
      IsPrintPrepLabel: this.selectedProduct.IsPrintPrepLabel,
      TerminalId: this.applicationStateService.terminalId,
      TerminalName: this.applicationStateService.terminalName,
      IsPrintPrepLabels: isPrintPrepLabels,
      UserId: this.applicationStateService.userId
    };
    this.spinnerService.show();
    this.inventoryProductService.recordInventoryPreparation(payload)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: () => {
          this.alertsService.renderSuccessMessage(Messages.PrepRecordAdded);
          this.resetRecipe();
        },
        error: this.alertsService.showApiError
      });
  }

  private saveVariable(isPrintPrepLabels: boolean) {
    const payload = {
      Qty: this.num(this.madeQty),
      RecipeList: this.recipeList,
      TerminalId: this.applicationStateService.terminalId,
      IsPrintPrepLabels: isPrintPrepLabels,
      UserId: this.applicationStateService.userId
    };
    this.spinnerService.show();
    this.variableIngredientRecipeService.prepareVariableInventory(payload)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: () => {
          this.alertsService.renderSuccessMessage(Messages.PrepRecordAdded);
          this.resetRecipe();
        },
        error: this.alertsService.showApiError
      });
  }

  printRecipe() {
    if (this.isVariable) {
      this.printVariable();
    } else {
      this.printFixed();
    }
  }

  private printFixed() {
    if (this.selectedProductId && this.num(this.qty)) {
      const payload: RecordInventoryPreparation = {
        InventoryProductId: this.selectedProductId,
        Qty: this.num(this.qty),
        Name: this.selectedProduct.Name,
        IsPrintPrepLabel: this.selectedProduct.IsPrintPrepLabel,
        TerminalId: this.applicationStateService.terminalId,
        TerminalName: this.applicationStateService.terminalName,
        IsPrintPrepLabels: false,
        UserId: this.applicationStateService.userId
      };
      this.spinnerService.show();
      this.inventoryProductService.printInventoryPrep(this.selectedProductId, payload)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: () => this.alertsService.renderSuccessMessage(Messages.PrintRequestSendSuccess),
          error: this.alertsService.showApiError
        });
    } else {
      this.showInfo(Messages.RequiredProductAndQtyForFixedPrepRecipe);
    }
  }

  private printVariable() {
    if (this.selectedProductId && this.prepareQty > 0) {
      const payload = {
        InventoryProductId: this.selectedProductId,
        TerminalId: this.applicationStateService.terminalId,
        TerminalName: this.applicationStateService.terminalName,
        Qty: this.prepareQty,
        InventoryProductRecipes: this.recipeList
      };
      this.spinnerService.show();
      this.variableIngredientRecipeService.printInventoryPrep(this.selectedProductId, payload)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: () => this.alertsService.renderSuccessMessage(Messages.PrintRequestSendSuccess),
          error: this.alertsService.showApiError
        });
    } else {
      this.showInfo(Messages.RequiredProductAndQtyForVariableRecipe);
    }
  }

  private confirm(message: string, onConfirm: () => void) {
    const modalRef = this.modalService.getModalWrapper(InfoModalComponent);
    const modal = modalRef.show({
      animated: false,
      class: 'vertical-center',
      initialState: {
        message,
        confirmButtonText: 'Yes',
        rejectButtonText: 'No',
        modalHeaderText: 'Confirm'
      }
    });
    modal.close.subscribe((response) => {
      if (response && response.shouldConfirm) { onConfirm(); }
    });
  }

  private showInfo(message: string) {
    const modalRef = this.modalService.getModalWrapper(InfoModalComponent);
    modalRef.show({ animated: false, class: 'vertical-center', initialState: { message } });
  }

  private num(value: any): number {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  private resetSelection() {
    this.recipeInformation = '';
    this.recipeList = [];
    this.isRecipeLoaded = false;
    this.isFormSubmitted = false;
    this.qty = null;
    this.unitName = '';
    this.prepareQty = 0;
    this.madeQty = null;
    this.variableUnit = '';
  }

  resetRecipe() {
    this.selectedProductId = null;
    this.selectedProduct = null;
    this.resetSelection();
  }

  cancel() {
    this.router.navigate(['manage/console']);
  }
}
